import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { getEvent } from '../../services/events'
import { listTicketTypes } from '../../services/ticketTypes'
import { initOrder } from '../../services/orders'
import { initializePayment, verifyPayment } from '../../services/payments'
import { isLogged } from '../../services/helpers'
import MobileBottomNav from '../../components/MobileBottomNav'
import {
  CalendarIcon,
  BuildingOffice2Icon,
  MapPinIcon
} from '@heroicons/react/24/outline'

const EventDetailPage = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [ticketTypes, setTicketTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [buying, setBuying] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info')
  const [quantities, setQuantities] = useState({})
  const [selectedTicketType, setSelectedTicketType] = useState(null)

  const fetchData = useCallback(async () => {
    if (!eventId) return
    setLoading(true)
    const evt = await getEvent(eventId)
    if (evt.status === 200) {
      setEvent(evt.data?.event || evt.data)
      setTicketTypes(evt.data?.ticketTypes || [])
      if (evt.data?.ticketTypes && evt.data.ticketTypes.length > 0) {
        setSelectedTicketType(evt.data.ticketTypes[0])
      }
    } else {
      const ticketsRes = await listTicketTypes(eventId)
      if (ticketsRes.status === 200) {
        setTicketTypes(ticketsRes.data || [])
        if (ticketsRes.data && ticketsRes.data.length > 0) {
          setSelectedTicketType(ticketsRes.data[0])
        }
      }
    }
    setLoading(false)
  }, [eventId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    // Initialize quantities to 1 for selected ticket type
    if (selectedTicketType) {
      const ticketId = selectedTicketType._id || selectedTicketType.id
      if (!quantities[ticketId]) {
        setQuantities({ ...quantities, [ticketId]: 1 })
      }
    }
  }, [selectedTicketType])

  useEffect(() => {
    // Check for payment success redirect and verify payment
    const urlParams = new URLSearchParams(window.location.search)
    const status = urlParams.get('status') || urlParams.get('payment')
    const orderId = urlParams.get('orderId')
    const txRef = urlParams.get('tx_ref') || urlParams.get('txRef')
    const transactionId = urlParams.get('transaction_id') || urlParams.get('transactionId')
    
    if ((status === 'successful' || status === 'success') && (orderId || txRef || transactionId)) {
      verifyAndProcessPayment(orderId, txRef, transactionId)
    } else if (status === 'cancelled' || status === 'failed') {
      setMessage('Payment was not completed. Please try again.')
      setMessageType('error')
    }
  }, [navigate])

  const verifyAndProcessPayment = async (orderId, txRef, transactionId) => {
    setMessage('Verifying payment...')
    setMessageType('info')
    setBuying(true)
    
    try {
      const verifyRes = await verifyPayment({ orderId, txRef, transactionId })
      
      if (verifyRes.status === 200) {
        setMessage('Payment successful! Your tickets have been issued. Redirecting...')
        setMessageType('success')
        setTimeout(() => {
          navigate('/account/tickets', { 
            state: { 
              message: 'Tickets purchased successfully!',
              orderId: orderId
            }
          })
        }, 2000)
      } else {
        setMessage('Payment verification pending. Please check your tickets page.')
        setMessageType('info')
        setTimeout(() => {
          navigate('/account/tickets')
        }, 3000)
      }
    } catch (err) {
      console.error('Payment verification error:', err)
      setMessage('Payment successful! Please check your tickets page.')
      setMessageType('info')
      setTimeout(() => {
        navigate('/account/tickets')
      }, 3000)
    } finally {
      setBuying(false)
    }
  }

  const handleQuantityChange = (change) => {
    if (!selectedTicketType) return
    const ticketId = selectedTicketType._id || selectedTicketType.id
    const currentQty = quantities[ticketId] || 1
    const maxQty = selectedTicketType?.remaining ?? selectedTicketType?.quantity ?? 999
    const newQty = Math.max(1, Math.min(maxQty, currentQty + change))
    setQuantities({ ...quantities, [ticketId]: newQty })
  }

  const handlePurchase = async () => {
    if (!selectedTicketType) return
    
    if (!isLogged()) {
      sessionStorage.setItem('pendingPurchase', JSON.stringify({ eventId, ticketId: selectedTicketType._id || selectedTicketType.id }))
      navigate('/login', { state: { from: `/events/${eventId}` } })
      return
    }

    if (!isLogged()) {
      setMessage('Please log in to purchase tickets.')
      navigate('/login')
      return
    }

    const ticketId = selectedTicketType._id || selectedTicketType.id
    const quantity = quantities[ticketId] || 1
    const maxQty = selectedTicketType?.remaining ?? selectedTicketType?.quantity ?? 999
    
    if (quantity < 1 || quantity > maxQty) {
      setMessage(`Please select a quantity between 1 and ${maxQty}`)
      setMessageType('error')
      return
    }

    setMessage('')
    setMessageType('info')
    setBuying(true)

    try {
      const orderRes = await initOrder({
        eventId,
        items: [{
          ticketTypeId: ticketId,
          quantity: quantity
        }],
        currency: selectedTicketType.currency || 'NGN',
        fees: 0
      })

      if ((orderRes.status !== 200 && orderRes.status !== 201) || !orderRes.data) {
        const errorMsg = orderRes.error || 'Failed to initialize order. Please try again.'
        setMessage(errorMsg)
        setMessageType('error')
        setBuying(false)
        return
      }

      const order = orderRes.data
      const redirectUrl = `${window.location.origin}/events/${eventId}?payment=success&orderId=${order._id || order.id}`
      const paymentRes = await initializePayment({
        orderId: order._id || order.id,
        eventId: eventId,
        redirectUrl: redirectUrl
      })

      if (paymentRes.status !== 200 || !paymentRes.data) {
        const errorMsg = paymentRes.error || 'Failed to initialize payment. Please try again.'
        setMessage(errorMsg)
        setMessageType('error')
        setBuying(false)
        return
      }

      const paymentLink = paymentRes.data.paymentLink || paymentRes.data.link
      if (paymentLink) {
        window.location.href = paymentLink
      } else {
        setMessage('Payment link not available. Please try again.')
        setMessageType('error')
        setBuying(false)
      }
    } catch (err) {
      console.error('Payment initialization error:', err)
      setMessage('Unable to start payment. Please try again.')
      setMessageType('error')
      setBuying(false)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: selectedTicketType?.currency || event?.currency || 'NGN',
      minimumFractionDigits: 0
    }).format(price)
  }

  const startsAt = event?.startsAt ? new Date(event.startsAt) : null
  const endsAt = event?.endsAt ? new Date(event.endsAt) : null
  const selectedQuantity = selectedTicketType ? (quantities[selectedTicketType._id || selectedTicketType.id] || 1) : 1
  const selectedTicketPrice = selectedTicketType?.price || 0
  const totalPrice = selectedTicketPrice * selectedQuantity
  const maxQty = selectedTicketType?.remaining ?? selectedTicketType?.quantity ?? 999
  const isAvailable = maxQty > 0

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading event...</p>
        </div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-900 mb-2">Event not found</p>
          <button
            onClick={() => navigate('/events')}
            className="text-emerald-600 hover:text-emerald-700 font-medium"
          >
            ← Back to events
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white pb-20 lg:pb-0">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/events')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to events
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Section */}
          <div>
            {event.coverImage ? (
              <div className="relative h-96 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl overflow-hidden mb-4">
                <img
                  src={event.coverImage}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="h-96 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl flex items-center justify-center">
                <CalendarIcon className="w-24 h-24 text-purple-300" />
              </div>
            )}
          </div>

          {/* Details Section */}
          <div>
            <p className="text-sm font-semibold text-purple-600 uppercase tracking-wide mb-2">
              Event
            </p>
            
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {event.title}
            </h1>

            {/* Center Info */}
            {event.centerId && (
              <div className="flex items-center gap-2 text-slate-600 mb-6 pb-6 border-b border-slate-200">
                <BuildingOffice2Icon className="w-5 h-5" />
                <span className="font-medium">{event.centerId.organization || 'Unknown Center'}</span>
              </div>
            )}

            {/* Date & Time */}
            {startsAt && (
              <div className="mb-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <CalendarIcon className="w-5 h-5" />
                  <span className="text-sm font-semibold">Date & Time</span>
                </div>
                <p className="text-slate-900 font-medium">
                  {startsAt.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                <p className="text-slate-600 text-sm">
                  {startsAt.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                  {endsAt && ` - ${endsAt.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}`}
                </p>
              </div>
            )}

            {/* Venue */}
            {event.venue && (
              <div className="mb-6">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <MapPinIcon className="w-5 h-5" />
                  <span className="text-sm font-semibold">Venue</span>
                </div>
                <p className="text-slate-900 font-medium">{event.venue}</p>
              </div>
            )}

            {/* Ticket Selection */}
            {ticketTypes.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="mb-4">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Ticket Type</label>
                  <select
                    value={selectedTicketType?._id || selectedTicketType?.id || ''}
                    onChange={(e) => {
                      const ticket = ticketTypes.find(tt => (tt._id || tt.id) === e.target.value)
                      setSelectedTicketType(ticket)
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    {ticketTypes.map(tt => (
                      <option key={tt._id || tt.id} value={tt._id || tt.id}>
                        {tt.name} - {formatPrice(tt.price || 0)} ({tt.remaining ?? tt.quantity ?? 0} left)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Availability */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-700">Availability</span>
                  <span className="text-sm font-semibold text-slate-900">{maxQty} left</span>
                </div>

                {/* Quantity Selector */}
                {isAvailable && (
                  <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Quantity:</span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(-1)}
                          disabled={selectedQuantity <= 1 || buying}
                          className="w-10 h-10 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold"
                        >
                          −
                        </button>
                        <span className="w-12 text-center text-lg font-semibold text-slate-900">{selectedQuantity}</span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(1)}
                          disabled={selectedQuantity >= maxQty || buying}
                          className="w-10 h-10 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Total:</span>
                        <span className="text-xl font-bold text-purple-600">
                          {formatPrice(totalPrice)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Message */}
            {message && (
              <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
                messageType === 'error' 
                  ? 'border-red-200 bg-red-50 text-red-800' 
                  : messageType === 'success'
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-emerald-100 bg-emerald-50 text-emerald-800'
              }`}>
                {message}
              </div>
            )}

            {/* Purchase Button */}
            {ticketTypes.length > 0 ? (
              isAvailable ? (
                <button
                  onClick={handlePurchase}
                  disabled={buying || !selectedTicketType}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {buying ? 'Processing...' : `Get Tickets (${selectedQuantity})`}
                </button>
              ) : (
                <div className="w-full bg-slate-200 text-slate-600 font-semibold py-4 px-6 rounded-lg text-center">
                  Sold Out
                </div>
              )
            ) : (
              <div className="w-full bg-slate-200 text-slate-600 font-semibold py-4 px-6 rounded-lg text-center">
                No tickets available
              </div>
            )}

            {/* Description */}
            <div className="mt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3">About this event</h2>
              <p className="text-slate-700 whitespace-pre-line leading-relaxed">
                {event.description}
              </p>
            </div>

            {/* Tags */}
            {event.tags && event.tags.length > 0 && (
              <div className="mt-6">
                <div className="flex flex-wrap gap-2">
                  {event.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <MobileBottomNav />
    </div>
  )
}

export default EventDetailPage
